<template>
  <div class="plans-grid">
    <div v-if="filteredPlans.length === 0" class="no-results">
        No plans match your filters.
    </div>

    <div v-for="plan in filteredPlans" :key="plan.hashId" class="plan-card">
        <div class="card-header-row">
            <div class="card-badges">
                <span class="badge time">{{ plan.timeCommitment }}m</span>
                <span class="badge pace">{{ plan.paceTag }}</span>
            </div>
            <button class="save-btn" @click.stop="toggleSave(plan)" :title="isSaved(plan) ? 'Unsave' : 'Save Plan'">
                 <svg v-if="isSaved(plan)" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#3B82F6" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
                 <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
            </button>
        </div>
        <h3 class="plan-title">{{ plan.name }}</h3>
        <p class="plan-desc">{{ plan.description }}</p>
        
        <div class="tags">
            <span v-for="tag in plan.focusTags" :key="tag" class="tag-chip">{{ tag }}</span>
        </div>
        
        <button class="view-btn" @click="viewPlan(plan.hashId)">View Plan</button>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { usePlanStore } from '../stores/plan';
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';

export default {
  name: 'PredefinedPlanCards',
  props: {
    filters: {
        type: Object,
        default: () => ({ time: null, focus: [], pace: null })
    }
  },
  setup(props) {
    const planStore = usePlanStore();
    const authStore = useAuthStore();
    const router = useRouter();
    const plansWithHashes = ref([]);

    onMounted(async () => {
        // Now using explicit IDs from the store definitions
        const processed = planStore.predefinedPlans.map(p => ({
            ...p,
            hashId: p.id // Use explicit ID ('one_year', etc)
        }));
        plansWithHashes.value = processed;
    });

    const filteredPlans = computed(() => {
        return plansWithHashes.value.filter(p => {
            // Time Filter
            if (props.filters.time && p.timeCommitment !== props.filters.time) return false;
            
            // Pace Filter
            if (props.filters.pace && p.paceTag !== props.filters.pace) return false;
            
            // Focus Filter (Interesection)
            if (props.filters.focus && props.filters.focus.length > 0) {
                // Let's go with: If any focus filter selected, plan must match at least one.
                // Match ALL selected tags (AND logic)
                const hasMatch = props.filters.focus.every(f => p.focusTags.includes(f));
                if (!hasMatch) return false;
            }
            
            return true;
        });
    });

    const viewPlan = (hash) => {
        router.push(`/plan/${hash}`);
    };

    const isSaved = (plan) => {
        if (!authStore.user || !authStore.savedPlans) return false;
        return authStore.savedPlans.some(p => p.id === plan.id);
    };

    const toggleSave = async (plan) => {
        if (!authStore.user) {
            router.push('/login');
            return;
        }
        await authStore.toggleSavePlan(plan);
    };

    return {
        filteredPlans,
        viewPlan,
        isSaved,
        toggleSave
    }
  }
}
</script>

<style scoped>
.plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
}

.no-results {
    color: #888;
    text-align: center;
    grid-column: 1 / -1;
    padding: 40px;
}

.plan-card {
    background: #1a1c1c;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s, border-color 0.2s;
}

.plan-card:hover {
    transform: translateY(-4px);
    border-color: rgb(var(--v-theme-primary));
}

.card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
}

.card-badges {
    display: flex;
    gap: 8px;
}

.save-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    color: #666;
    transition: all 0.2s;
}

.save-btn:hover {
    transform: scale(1.1);
}

.badge {
    font-size: 0.75rem;
    text-transform: uppercase;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
}

.badge.time {
    background: rgba(50, 184, 198, 0.2);
    color: rgb(var(--v-theme-primary));
}

.badge.pace {
    background: rgba(255, 255, 255, 0.1);
    color: #ccc;
}

.plan-title {
    font-size: 1.25rem;
    color: #fff;
    margin-bottom: 8px;
}

.plan-desc {
    color: #999;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 20px;
    flex: 1;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 24px;
}

.tag-chip {
    font-size: 0.8rem;
    color: #aaa;
    border: 1px solid #333;
    padding: 2px 8px;
    border-radius: 12px;
}

.view-btn {
    width: 100%;
    padding: 12px;
    background: rgb(var(--v-theme-primary));
    color: #0f1111;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
}

.view-btn:hover {
    opacity: 0.9;
}
</style>
